import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { DurationModal } from 'js/src/components/common/DurationModal';
import { getVideoDraftById } from 'js/src/data/video-planner';
import type { RootState } from 'js/src/libs/RootState';
import { getTimeStringFromSeconds } from 'js/src/libs/video-duration-tools';

interface VideoDraftDurationModalProps {
    videoDraftId: string;
    sendMessage: (message: string) => Promise<boolean>;
    isOpen: boolean;
    onClose: () => void;
}

const findClosestDuration = (targetSeconds: number, availableOptions: number[]): number => {
    return availableOptions.reduce((closest, current) => {
        return Math.abs(current - targetSeconds) < Math.abs(closest - targetSeconds)
            ? current
            : closest;
    });
};

export const VideoDraftDurationModal: React.FC<VideoDraftDurationModalProps> = ({
    videoDraftId,
    sendMessage,
    isOpen,
    onClose,
}) => {
    const videoDraft = useSelector((state: RootState) => getVideoDraftById(state, videoDraftId));

    const initialDuration = useMemo(() => {
        const currentDurationMs = videoDraft?.luminary.duration;
        if (currentDurationMs === null || currentDurationMs === undefined) {
            return 15;
        }
        const availableDurations = Array.from({ length: 20 }, (_, i) => (i + 1) * 15);
        return findClosestDuration(currentDurationMs / 1000, availableDurations);
    }, [videoDraft?.luminary.duration]);

    const handleApply = useCallback(
        (selectedDuration: number) => {
            if (!videoDraft) return;
            void sendMessage(
                `Change the video duration to ${getTimeStringFromSeconds(selectedDuration)}`,
            );

            analytics.track('Sketch: Video Action - Change Duration', {
                video_title: videoDraft.name,
                duration_target_seconds: selectedDuration,
            });

            onClose();
        },
        [sendMessage, videoDraft, onClose],
    );

    if (!videoDraft) {
        return null;
    }

    return (
        <DurationModal
            isOpen={isOpen}
            onClose={onClose}
            onApply={handleApply}
            initialDuration={initialDuration}
            title="Change duration target"
            upgradeAnalyticsEvent="Sketch: Duration Modal - Upgrade Clicked"
        />
    );
};
